import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { useEdition, useMediaQuery } from "@/hooks/useEdition";
import { ReaderContext } from "@/components/magazine/ReaderContext";
import { Spread } from "@/components/reader/Spread";
import { TopBar, Dock } from "@/components/reader/ReaderChrome";
import { ContentsDrawer } from "@/components/reader/ContentsDrawer";
import { TextView } from "@/components/reader/TextView";
import { MediaPlayer } from "@/components/reader/MediaPlayer";
import { SharePanel } from "@/components/reader/SharePanel";
import { trackRead, downloadPdf, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";

const buildSpreads = (pages, double) => {
  if (!double) return pages.map((p) => [p]);
  const out = [[null, pages[0]]];
  for (let i = 1; i < pages.length; i += 2) out.push([pages[i], pages[i + 1] || null]);
  return out;
};

export default function Reader() {
  const { lang: langParam } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { edition, pages, isLoading, error, lang } = useEdition(langParam);
  const isDesktop = useMediaQuery("(min-width: 900px)");
  const [spreadPref, setSpreadPref] = useState(true);
  const double = isDesktop && spreadPref;
  const rtl = edition?.dir === "rtl";
  const [zoom, setZoom] = useState(1);
  const [contents, setContents] = useState(false);
  const [textView, setTextView] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [media, setMedia] = useState(null);
  const [share, setShare] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const dirRef = useRef(1);

  const onDownload = async () => {
    if (downloading || !edition) return;
    setDownloading(true);
    const t = toast.loading(ui.preparing || "Preparing PDF…", { description: "48 pages · high resolution", duration: Infinity });
    try {
      await downloadPdf(edition.slug, lang);
      toast.success(ui.download || "Download PDF", { id: t, description: `ROOTS-Edition-${edition.slug}-${lang.toUpperCase()}.pdf`, duration: 4000 });
    } catch (e) {
      toast.error(formatApiError(e), { id: t, duration: 6000 });
    } finally { setDownloading(false); }
  };

  const current = Math.min(Math.max(parseInt(params.get("p") || "1", 10) || 1, 1), pages.length || 1);
  const spreads = useMemo(() => buildSpreads(pages, double), [pages, double]);
  const index = useMemo(() => Math.max(0, spreads.findIndex((s) => s.some((p) => p && p.n === current))), [spreads, current]);
  const spread = spreads[index] || [];
  const visible = spread.filter(Boolean);
  const ui = edition?.ui || {};

  const goTo = useCallback((n) => {
    dirRef.current = n >= current ? 1 : -1;
    setParams({ p: String(n) }, { replace: false });
    setContents(false);
  }, [current, setParams]);

  const step = useCallback((d) => {
    const next = spreads[index + d];
    if (!next) return;
    dirRef.current = d;
    const target = next.find(Boolean);
    setParams({ p: String(target.n) });
  }, [spreads, index, setParams]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") step(rtl ? -1 : 1);
      else if (e.key === "ArrowLeft") step(rtl ? 1 : -1);
      else if (e.key === "Escape") { setContents(false); setTextView(false); setMedia(null); setShare(false); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step, rtl]);

  useEffect(() => { if (edition) trackRead(edition.slug, lang, current); }, [edition, lang, current]);
  useEffect(() => { document.documentElement.lang = lang; document.documentElement.dir = rtl ? "rtl" : "ltr"; }, [lang, rtl]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen?.().then(() => setFullscreen(true));
    else document.exitFullscreen?.().then(() => setFullscreen(false));
  };

  const label = visible.length ? `${visible.map((p) => String(p.n).padStart(2, "0")).join(" – ")} / ${pages.length}` : "";

  return (
    <ReaderContext.Provider value={{ goTo, openMedia: setMedia, ui, edition, interactive: true }}>
      <div className="reader-root" data-testid="reader-root" dir={rtl ? "rtl" : "ltr"}>
        <TopBar edition={edition} ui={ui} lang={lang} onLang={(l) => navigate(`/read/${l}?p=${current}`)} textView={textView} onTextView={() => setTextView((v) => !v)} fullscreen={fullscreen} onFullscreen={toggleFullscreen} />
        {isLoading && <div className="flex-1 flex items-center justify-center reader-label" data-testid="reader-loading">Loading…</div>}
        {error && <div className="flex-1 flex items-center justify-center reader-label text-red-300" data-testid="reader-error">Could not load the edition.</div>}
        {!isLoading && !error && pages.length > 0 && (
          <Spread spread={spread} index={`${double}-${index}`} direction={dirRef.current} zoom={zoom} onSwipe={(d) => step(rtl ? -d : d)} />
        )}
        <AnimatePresence>
          {contents && <ContentsDrawer key="drawer" pages={pages} edition={edition} ui={ui} current={visible.map((p) => p.n)} onGo={goTo} onClose={() => setContents(false)} />}
          {textView && <TextView key="text" pages={visible} edition={edition} ui={ui} onClose={() => setTextView(false)} />}
          {media && <MediaPlayer key="media" cta={media} ui={ui} onClose={() => setMedia(null)} />}
          {share && visible[0] && <SharePanel key="share" page={visible[visible.length - 1]} edition={edition} ui={ui} onClose={() => setShare(false)} />}
        </AnimatePresence>
        <Dock
          ui={ui} label={label} dir={edition?.dir}
          canPrev={index > 0} canNext={index < spreads.length - 1}
          onPrev={() => step(-1)} onNext={() => step(1)}
          zoom={zoom} onZoom={(d) => setZoom((z) => Math.min(2.5, Math.max(1, +(z + d * 0.5).toFixed(2))))}
          contents={contents} onContents={() => setContents((v) => !v)}
          spreadMode={spreadPref} onSpreadMode={() => setSpreadPref((v) => !v)} showSpreadToggle={isDesktop}
          onShare={() => setShare(true)}
          onDownload={onDownload} downloading={downloading}
        />
        <Toaster theme="dark" position="top-center" toastOptions={{ style: { background: "#1c1c1c", border: "1px solid rgba(217,218,221,0.2)", color: "#D9DADD", fontFamily: "var(--font-body)" } }} />
        <div className="sr-only" aria-live="polite">{ui.page} {label}</div>
      </div>
    </ReaderContext.Provider>
  );
}
